import React, { useState } from 'react';
import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    TextField,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import { IStorefrontSection, IStorefrontStyleSaveRequest } from '../../../../models/Storefront';
import { useSaveStorefrontStyleMutation } from '../../../../store/endpoints/StorefrontStyleEndpoints';
import { useStorefrontStyles } from '../StorefrontStylesContext';
import { GLOBAL_STYLE_SCOPE, isGlobalStyle, nextSortOrder, sortedStyles } from '../stylesConfig';
import { parseHomePageConfig } from '../homePageConfig';
import {
    capturedSectionParts,
    currentSectionStyle,
    sectionHasAppearance,
    sectionWithStyleRef,
} from './sectionStyleRef';

// The two whole-section style commands, in the section editor's command area next to "Apply Appearance to
// All Sections".
//
// The Appearance tab already attaches a named style to ONE bag at a time, which is the right control for
// tuning a part. Neither of these is that: saving a section's look, and giving a section somebody else's
// look, are operations on the whole section, and doing either through four separate dropdowns is how a
// reusable style stops being worth making.
//
// Saving writes through the styles API immediately, like every other style write (the Styles panel does the
// same) — NOT through the section draft, which only persists on Exit Edit Mode. The section's own repoint is
// a draft edit like any other, so the two halves land through different channels on purpose.

interface ISectionStyleActionsProps {
    section: IStorefrontSection;
    onChange: (section: IStorefrontSection) => void;
    /** The storefront's raw homePageConfig JSON, so a capture can stamp the page-level styling. */
    homePageConfig?: string;
    /** Closes the command callout once a dialog has done its work. */
    onDone?: () => void;
}

const SectionStyleActions: React.FC<ISectionStyleActionsProps> = ({
    section, onChange, homePageConfig, onDone,
}) => {
    const { styles, storefrontId, canEdit } = useStorefrontStyles();
    const [saveStyle] = useSaveStorefrontStyleMutation();

    const [saving, setSaving] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const [name, setName] = useState('');
    // Updating the style the section already follows, versus saving a second one. Update is the default
    // when there is something to update: "save it again" almost always means refining the style, and the
    // create-only path quietly produced duplicates under an identical name, each repointing the section
    // at the newest copy and orphaning the one before it.
    const [saveAsNew, setSaveAsNew] = useState(false);
    // Updating a GLOBAL style reaches every storefront using it, so it is confirmed rather than assumed —
    // the same guard the Styles panel puts on saving a global.
    const [confirmGlobal, setConfirmGlobal] = useState(false);
    // Saved straight into the organization's global scope instead of this storefront's. The Styles panel can
    // re-scope a style afterwards ("Move to Global"), but a style made to be reused on OTHER storefronts —
    // which is the whole reason to save one — should not need a second trip through a different panel to
    // become usable.
    const [global, setGlobal] = useState(false);
    const [error, setError] = useState<string>();

    const [useOpen, setUseOpen] = useState(false);
    const [pickedId, setPickedId] = useState<string>();

    // What the section is currently sitting in. A capture stamps these page-level layers into the style,
    // so the style carries the look the section actually has and not just its container (AC-1165).
    const pageConfig = React.useMemo(
        () => parseHomePageConfig(homePageConfig) as unknown as { [configKey: string]: unknown },
        [homePageConfig]);

    const sectionStyles = sortedStyles((styles ?? []).filter((s) => s.elementType === 'section'));
    // Nothing to save from a section with no styling of its own, and nothing to apply when the organization
    // keeps no section styles — in both cases the command is left out rather than shown and then refused.
    // The style this section already follows, if any. Its presence is what makes updating possible.
    const followed = currentSectionStyle(section, sectionStyles);
    const updating = !!followed && !saveAsNew;
    const canSave = !!canEdit && !!storefrontId && sectionHasAppearance(section, styles, pageConfig);
    const canUse = !!canEdit && sectionStyles.length > 0;
    if (!canSave && !canUse) return null;

    const newStyleName = () => (section.title ? `${section.title} Section` : 'New Section Style');

    const openSave = () => {
        setSaveAsNew(false);
        // The style's OWN name when updating, so the field is not silently renaming what it saves.
        setName(followed?.name ?? newStyleName());
        setGlobal(false);
        setError(undefined);
        setSaveOpen(true);
    };

    // Switching between updating and saving a new one re-seeds the name, or the new style inherits the
    // name of the one it was going to replace.
    const setMode = (asNew: boolean) => {
        setSaveAsNew(asNew);
        setName(asNew ? newStyleName() : followed?.name ?? newStyleName());
    };

    const requestSave = () => {
        // Confirm first when this would rewrite a style every storefront shares.
        if (updating && isGlobalStyle(followed)) setConfirmGlobal(true);
        else void commitSave();
    };

    const commitSave = async () => {
        setConfirmGlobal(false);
        const trimmed = name.trim();
        if (!trimmed || !storefrontId) return;
        setSaving(true);
        setError(undefined);
        try {
            // The style being written, when this is an update. Narrowed once rather than asserted at
            // each use — `updating` folds in the saveAsNew flag, which the compiler cannot see through.
            const target = updating ? followed : undefined;
            // An update keeps the style where it already lives; only a new one is scoped by the checkbox.
            const scope = target ? (target.storefront ?? storefrontId) : (global ? GLOBAL_STYLE_SCOPE : storefrontId);
            const body: IStorefrontStyleSaveRequest = {
                // With an id the mutation PUTs and the server updates in place; without one it POSTs and
                // the server issues a fresh id (it refuses a client-supplied one outright).
                id: target?.id,
                name: trimmed,
                elementType: 'section',
                storefront: scope,
                sortOrder: target ? target.sortOrder : nextSortOrder(styles ?? [], 'section'),
                // Deliberately NOT excluding the style being updated: capture reads the section's EFFECTIVE
                // look, which is that style's parts plus whatever local edits sit on top, and folding those
                // edits back in is exactly what updating means. Excluding it would drop every value the
                // local edits did not happen to restate.
                parts: capturedSectionParts(section, styles, pageConfig),
            };
            // Invalidated against THIS storefront whatever the scope, because this storefront's list is the
            // one on screen and a global style belongs in it too.
            const result = await saveStyle({ style: body, storefront: storefrontId }).unwrap();
            // A save can answer 200 and still refuse; checked rather than assumed, so a refusal cannot end
            // with the section pointed at a style that was never created.
            if (result?.success === false || !result?.id) {
                setError(result?.message || 'The style could not be saved. Nothing has been changed.');
                return;
            }
            // Point the section at what was just saved. Visually a no-op — the style's parts ARE this
            // section's current appearance — but it makes the section follow the style from here on, which
            // is the difference between saving a copy and having a style at all. Without it the merchant
            // ends up editing two things that silently drift apart.
            // Re-pointed either way. After an update the section's local edits have been folded into the
            // style, so leaving them in place would have it overriding the style with the very values the
            // style now holds — and the next update would look like a no-op.
            onChange(sectionWithStyleRef(section, result.id));
            toast.success(updating
                ? `Updated "${trimmed}".`
                : `Saved "${trimmed}" and pointed this section at it.`);
            setSaveOpen(false);
            onDone?.();
        } catch {
            setError('The style could not be saved. Nothing has been changed.');
        } finally {
            setSaving(false);
        }
    };

    const options: IDropdownOption[] = sectionStyles.map((s) => ({
        key: s.id,
        text: isGlobalStyle(s) ? `${s.name} (Global)` : s.name,
    }));

    const commitUse = () => {
        if (!pickedId) return;
        onChange(sectionWithStyleRef(section, pickedId));
        setUseOpen(false);
        onDone?.();
    };

    return (
        <>
            {canUse && (
                <DefaultButton
                    style={{ width: '100%' }}
                    text='Use a Section Style…'
                    iconProps={{ iconName: 'Color' }}
                    onClick={() => { setPickedId(undefined); setUseOpen(true); }}
                />
            )}
            {canSave && (
                <DefaultButton
                    style={{ width: '100%' }}
                    text='Save Appearance as a Section Style…'
                    iconProps={{ iconName: 'Save' }}
                    onClick={openSave}
                />
            )}

            <Dialog
                hidden={!saveOpen}
                onDismiss={() => setSaveOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: updating ? 'Update this section style' : 'Save as a section style',
                    subText: updating
                        ? 'This section already follows a saved style. Its current appearance — including '
                            + 'anything changed since — is written back to that style, so every section '
                            + 'following it changes too.'
                        : 'This section\'s appearance becomes a reusable style you can apply to any other '
                            + 'section. This section will follow it from now on, so it looks exactly the same '
                            + 'today and changes with the style later.',
                }}
            >
                <TextField
                    label='Name'
                    value={name}
                    autoFocus
                    onChange={(_e, v) => setName(v ?? '')}
                    onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) requestSave(); }}
                />
                {/* Only offered when there is something to update. Without it, saving twice made two
                    styles with the same name and no way to tell them apart. */}
                {!!followed && (
                    <Checkbox
                        label={`Save as a new style instead of updating "${followed.name}"`}
                        checked={saveAsNew}
                        onChange={(_e, checked) => setMode(!!checked)}
                        styles={{ root: { marginTop: '0.75em' } }}
                    />
                )}
                {/* Said plainly rather than left to be discovered: the card styling is genuinely not part of
                    what gets saved, because a section style has nowhere to put it that anything reads. */}
                {/* Scope is only a choice for a NEW style — an update leaves the style where it lives. */}
                {!updating && (
                    <Checkbox
                        label='Make it available to every storefront'
                        checked={global}
                        onChange={(_e, checked) => setGlobal(!!checked)}
                        styles={{ root: { marginTop: '0.75em' } }}
                    />
                )}
                {/* Worth stating, because it is the one thing here that does not behave like the rest of the
                    pages editor: a global style has no staged copy, so it is not part of the draft and is
                    not waiting on Publish. */}
                {global && !updating && (
                    <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: '0.5em' } }}>
                        Global styles are saved live rather than staged in the draft, so this one is available
                        to every storefront straight away and Revert will not undo it.
                    </MessageBar>
                )}
                <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: '0.75em' } }}>
                    The section, header and footer styling is saved. Product card styling stays on the
                    section — that is a product card style, not a section one.
                </MessageBar>
                {error && (
                    <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginTop: '0.5em' } }}>
                        {error}
                    </MessageBar>
                )}
                <DialogFooter>
                    <PrimaryButton
                        text={saving ? 'Saving...' : (updating ? 'Update style' : 'Save style')}
                        disabled={saving || !name.trim()}
                        onClick={requestSave}
                        onRenderIcon={saving ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
                    />
                    <DefaultButton text='Cancel' iconProps={{ iconName: 'Cancel' }} disabled={saving} onClick={() => setSaveOpen(false)} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!confirmGlobal}
                onDismiss={() => setConfirmGlobal(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Update global style',
                    subText: `"${followed?.name}" is a Global Style — this rewrites it for every storefront `
                        + 'using it, immediately, and Revert will not undo it.',
                }}
            >
                <DialogFooter>
                    <PrimaryButton text='Update it' onClick={() => { void commitSave(); }} />
                    <DefaultButton text='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={() => setConfirmGlobal(false)} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!useOpen}
                onDismiss={() => setUseOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Use a section style',
                    subText: 'The section keeps its title, content and products. Its own section, header and '
                        + 'footer styling is replaced by the style, so the style is what you see.',
                }}
            >
                <Dropdown
                    label='Section style'
                    options={options}
                    selectedKey={pickedId}
                    onChange={(_e, o) => setPickedId(o?.key as string)}
                />
                <DialogFooter>
                    <PrimaryButton text='Use this style' disabled={!pickedId} onClick={commitUse} />
                    <DefaultButton text='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={() => setUseOpen(false)} />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default SectionStyleActions;
